import { useEffect, useMemo, useRef, useState } from "react";
import { addDays, addWeeks, format } from "date-fns";
import { toast } from "sonner";
import { Copy, RotateCcw, Save, UtensilsCrossed } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { DAY_LABELS } from "@/lib/constants";
import { defaultBlocks, fmtDuration, hhmm, hoursFor, minutesOf, toDay, weekDays, weekStartOf } from "@/lib/time";
import DayEditor, { H_END, H_PX, H_START } from "@/components/hours/DayEditor";

const btn = "inline-flex items-center gap-2 rounded-full border border-[#103080]/20 bg-white/70 text-[#103080] font-medium px-4 h-10 text-sm hover:border-[#0090D0] hover:text-[#0090D0] transition-colors duration-200";

function cutLunch(blocks, h) {
  if (!h.lunch_start || !h.lunch_end || h.lunch_start >= h.lunch_end) return blocks;
  const L1 = minutesOf(h.lunch_start);
  const L2 = minutesOf(h.lunch_end);
  return blocks.flatMap((b) => {
    const a = minutesOf(b.start);
    const e = minutesOf(b.end);
    if (e <= L1 || a >= L2) return [b];
    return [[a, L1], [L2, e]].filter(([x, y]) => y - x >= 15).map(([x, y]) => ({ start: hhmm(x), end: hhmm(y) }));
  });
}

const total = (days, draft) => days.reduce((sum, d) => sum + (draft[toDay(d)] || []).reduce((s, b) => s + minutesOf(b.end) - minutesOf(b.start), 0), 0);

export default function Hours() {
  const { user } = useAuth();
  const s = user.settings;
  const first = useMemo(() => weekStartOf(new Date()), []);
  const weeks = [0, 1, 2, 3].map((i) => addWeeks(first, i));
  const [active, setActive] = useState(0);
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const scroller = useRef(null);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  useEffect(() => {
    if (!draft || !scroller.current) return;
    const starts = s.work_days.map((d) => minutesOf(hoursFor(s, d).start));
    scroller.current.scrollTop = Math.max(0, ((Math.min(...starts, 9 * 60) - 30 - H_START) / 60) * H_PX);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(draft)]);

  useEffect(() => {
    api.get("/availability", { params: { start: toDay(first), days: 28 } }).then(({ data }) => {
      const init = {};
      for (let i = 0; i < 28; i += 1) {
        const d = addDays(first, i);
        init[toDay(d)] = data[toDay(d)] ?? defaultBlocks(d, s);
      }
      setDraft(init);
    }).catch((e) => toast.error(formatApiError(e)));
  }, [first, s]);

  if (!draft) return <p className="text-[#103080]/60">Loading…</p>;
  const days = weekDays(weeks[active]);
  const update = (patch) => { setDraft((p) => ({ ...p, ...patch })); setDirty(true); };
  const mapWeek = (fn) => update(Object.fromEntries(days.map((d) => [toDay(d), fn(d)])));
  const copyForward = () => {
    const patch = {};
    weeks.slice(active + 1).forEach((w) => weekDays(w).forEach((d, i) => { patch[toDay(d)] = draft[toDay(days[i])]; }));
    update(patch);
    toast.success("Copied this week's hours to the following weeks");
  };
  const save = async () => {
    try {
      await api.put("/availability", { days: draft });
      setDirty(false);
      toast.success("Working hours saved. Your planner will fit tasks into these boxes.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="hours-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#0090D0] font-semibold mb-3">My hours</p>
          <h1 className="font-serif-brand font-semibold text-[#103080] text-4xl sm:text-5xl">When I'm <i className="text-[#0090D0]">working</i></h1>
          <p className="mt-3 text-[#0F172A]/60 max-w-xl">Drag on a day to draw a working box. Drag a box to move it, or pull its top or bottom edge to resize. Gaps between boxes, like lunch, stay free.</p>
        </div>
        <button onClick={save} disabled={!dirty} data-testid="hours-save-button"
          className="inline-flex items-center gap-2 rounded-full bg-[#103080] text-white font-medium px-6 h-11 text-sm hover:bg-[#0090D0] transition-colors duration-200 disabled:opacity-50 shrink-0">
          <Save size={16} /> {dirty ? "Save hours" : "Saved"}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {weeks.map((w, i) => (
          <button key={i} onClick={() => setActive(i)} data-testid={`hours-week-tab-${i}`}
            className={`rounded-full px-4 h-10 text-sm font-semibold transition-colors duration-200 ${active === i ? "bg-[#103080] text-white" : "bg-[#F5F2EB] text-[#103080] hover:bg-[#103080]/10"}`}>
            {i === 0 ? "This week" : `w/c ${format(w, "d MMM")}`} · {fmtDuration(total(weekDays(w), draft))}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <button className={btn} onClick={() => mapWeek((d) => cutLunch(draft[toDay(d)], hoursFor(s, (d.getDay() + 6) % 7)))} data-testid="hours-add-lunch-button"><UtensilsCrossed size={15} /> Add my usual lunch breaks</button>
        <button className={btn} onClick={() => mapWeek((d) => defaultBlocks(d, s))} data-testid="hours-reset-week-button"><RotateCcw size={15} /> Reset to my usual week</button>
        {active < 3 && <button className={btn} onClick={copyForward} data-testid="hours-copy-forward-button"><Copy size={15} /> Copy to following weeks</button>}
      </div>

      {dirty && (
        <p className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="hours-unsaved-warning">
          You have unsaved changes. Click <b>Save hours</b> so your planner uses them.
        </p>
      )}

      <section className="rounded-2xl border border-[#103080]/10 bg-white/70 ambient overflow-x-auto thin-scroll" data-testid="hours-grid">
        <div className="min-w-[760px]">
          <div className="grid border-b border-[#103080]/10 bg-[#F5F2EB]/60" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
            <div />
            {days.map((d, i) => (
              <div key={i} className="px-3 py-3 border-l border-[#103080]/10 flex items-end justify-between">
                <div className="text-[#103080]">
                  <p className="text-[11px] uppercase tracking-[0.2em] font-semibold opacity-70">{DAY_LABELS[i]}</p>
                  <p className="font-serif-brand text-xl font-semibold">{format(d, "d MMM")}</p>
                </div>
                <button onClick={() => update({ [toDay(d)]: [] })} data-testid={`hours-day-off-${toDay(d)}`}
                  className="text-[11px] font-semibold text-[#103080]/50 hover:text-red-600 transition-colors duration-200">Day off</button>
              </div>
            ))}
          </div>
          <div ref={scroller} className="grid max-h-[600px] overflow-y-auto thin-scroll" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }} data-testid="hours-scroll">
            <div>
              {Array.from({ length: (H_END - H_START) / 60 }, (_, i) => (
                <div key={i} className="text-[11px] text-[#0F172A]/40 pr-2 text-right" style={{ height: H_PX }}>{hhmm(H_START + i * 60)}</div>
              ))}
            </div>
            {days.map((d) => (
              <DayEditor key={toDay(d)} dayKey={toDay(d)} blocks={draft[toDay(d)] || []} onChange={(b) => update({ [toDay(d)]: b })} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
